import { AuthorizedRoute } from '@/components/AuthorizedRoute/AuthorizedRoute'
import { DataTag } from '@/components/DataTag/DataTag'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import { useFindOrgById } from '@/generated/http-clients/v2ApiComponents'
import {
  useGetCluster,
  useGetClusterSubscription,
} from '@/generated/http-clients/v3ApiComponents'
import { clustersRootRoute } from '@/router/rootRoutes'
import { Text, VStack } from '@chakra-ui/react'
import { Outlet, createRoute, useLocation } from '@tanstack/react-router'
import get from 'lodash/get'
import { ChevronLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'
import type { SidebarSection } from '../../components/ClusterInfoSidebar/ClusterInfoSidebar'
import ClusterInfoSidebar from '../../components/ClusterInfoSidebar/ClusterInfoSidebar'

export const clusterConfigsRootRoute = createRoute({
  getParentRoute: () => clustersRootRoute,
  path: '$orgId/$clusterId/revision',
  component: () => (
    <AuthorizedRoute>
      <ClusterConfigsPage />
    </AuthorizedRoute>
  ),
})

function ClusterConfigsPage() {
  const pathParams = clusterConfigsRootRoute.useParams()
  const location = useLocation()

  const cluster = useGetCluster({ pathParams })
  const organization = useFindOrgById({
    pathParams: {
      orgId: pathParams.orgId,
    },
  })

  const { t } = useTranslation(undefined)
  const noData = t('general.notAvailable')

  const {
    data: subscriptionData,
    isSuccess: subscriptionIsSuccess,
    isLoading: subscriptionIsLoading,
    isError: subscriptionIsError,
  } = useGetClusterSubscription({
    pathParams: {
      orgId: pathParams.orgId,
      clusterId: pathParams.clusterId,
    },
  })

  const withSidebar = !location.pathname.includes('compare')
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)

  // Prepare sections for the sidebar
  const sidebarSections: SidebarSection[] = [
    // Overview section
    {
      title: '',
      data: [
        {
          key: 'Hive ID',
          value: (
            <DataTag
              value={cluster.data?.system?.global?.hive_id}
              maxWidthPx={160}
            >
              {cluster.data?.system?.global?.hive_id}
            </DataTag>
          ),
        },
        {
          key: 'Hive Name',
          value: (
            <DataTag
              value={cluster.data?.system?.global?.name}
              maxWidthPx={160}
            >
              {cluster.data?.system?.global?.name}
            </DataTag>
          ),
        },
        {
          key: 'Organization ID',
          value: (
            <DataTag value={organization.data?.org_id} maxWidthPx={160}>
              {organization.data?.org_id}
            </DataTag>
          ),
          link: {
            to: hivesOverviewOrganizationDetailsRoute.to,
            params: { orgId: pathParams.orgId },
            tooltip: 'Go to Organization',
          },
        },
        {
          key: 'Organization Name',
          value: (
            <DataTag value={organization.data?.org_name} maxWidthPx={160}>
              {organization.data?.org_name}
            </DataTag>
          ),
        },
        {
          key: 'Template ID',
          value: cluster.data?.template_id,
          link: {
            to: '/templates/$templateId',
            params: { templateId: cluster.data?.template_id ?? '' },
            tooltip: 'Go to template',
          },
        },
        {
          key: 'Product ID',
          // note: the line below should be cluster.data?.product_id, as this information should be provided in ClusterHead.
          // But it's not available at the moment so leaving this way will make this work when the information becomes available
          value: get(cluster.data, 'product_id'),
        },
        {
          key: 'Deployment Label',
          value: cluster.data?.system?.global?.deployment_label,
        },
        {
          key: 'Cloud Provider',
          value: cluster.data?.system?.global?.cloud_provider,
        },
      ],
    },
    // Applied Revision section
    {
      title: 'Applied Revision',
      data: [
        {
          key: 'Revision ID',
          value: (
            <DataTag
              value={cluster.data?.rollout?.revision_id}
              maxWidthPx={160}
            >
              {cluster.data?.rollout?.revision_id}
            </DataTag>
          ),
        },
        {
          key: 'Rollout State',
          value: (
            <>
              <Text>{cluster.data?.rollout?.state ?? noData}</Text>
              {cluster.data?.rollout?.state_message && (
                <Text color="text.text-subtle" fontSize="sm">
                  {cluster.data?.rollout?.state_message}
                </Text>
              )}
            </>
          ),
        },
        {
          key: 'Hive State',
          value: cluster.data?.hive_state?.state,
        },
        {
          key: 'Created By',
          value: cluster.data?.rollout?.created_by,
          external: false,
        },
        {
          key: 'Approved By',
          value: cluster.data?.rollout?.approved_by,
          external: false,
        },
        {
          key: 'Created At',
          value: cluster.data?.rollout?.created_at,
        },
        {
          key: 'Updated At',
          value: cluster.data?.rollout?.updated_at,
        },
        {
          key: 'Version',
          value: cluster.data?.rollout?.version,
        },
      ],
    },
  ]

  // Add subscription section if data is available
  if (subscriptionIsSuccess && subscriptionData) {
    sidebarSections.push({
      title: 'Subscription',
      data: [
        {
          key: 'Subscription ID',
          value: (
            <DataTag value={subscriptionData.subscriptionId} maxWidthPx={160}>
              {subscriptionData.subscriptionId}
            </DataTag>
          ),
        },
        {
          key: 'Customer ID',
          value: (
            <DataTag value={subscriptionData.customerId} maxWidthPx={160}>
              {subscriptionData.customerId}
            </DataTag>
          ),
        },
        {
          key: 'Status',
          value: subscriptionData.status,
        },
        {
          key: 'Start Date',
          value: subscriptionData.startDate,
        },
        {
          key: 'Trial Start',
          value: subscriptionData.trialStart,
        },
        {
          key: 'Trial End',
          value: subscriptionData.trialEnd,
        },
        {
          key: 'End Date',
          value: subscriptionData.endedAt,
        },
      ],
    })
  } else if (subscriptionIsLoading) {
    sidebarSections.push({
      title: 'Subscription',
      data: [
        {
          key: 'Status',
          value: (
            <Text color="text.text-subtle">Loading subscription data...</Text>
          ),
        },
      ],
    })
  } else if (subscriptionIsError) {
    sidebarSections.push({
      title: 'Subscription',
      data: [
        {
          key: 'Status',
          value: (
            <Text color="red.500" fontWeight="bold">
              Could not load subscription data
            </Text>
          ),
        },
      ],
    })
  }

  return (
    <PanelGroup direction="horizontal" style={{ gap: 16 }}>
      <Panel
        defaultSize={isSidebarVisible ? 67 : 100}
        minSize={50}
        maxSize={90}
        style={{ overflow: 'auto' }}
      >
        <VStack w="100%" alignItems="stretch">
          <Outlet />
        </VStack>
      </Panel>

      {withSidebar && isSidebarVisible && (
        <>
          <PanelResizeHandle />
          <Panel defaultSize={33} minSize={10} maxSize={50}>
            <ClusterInfoSidebar
              sections={sidebarSections}
              onClose={() => setIsSidebarVisible(false)}
            />
          </Panel>
        </>
      )}

      {withSidebar && !isSidebarVisible && (
        <button
          type="button"
          onClick={() => setIsSidebarVisible(true)}
          style={{
            position: 'fixed',
            top: '100px',
            right: '10px',
            background: 'white',
            border: '1px solid var(--chakra-colors-gray-300)',
            borderRadius: '50%',
            padding: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            color: 'var(--chakra-colors-blue-500)',
            zIndex: 10,
          }}
        >
          <ChevronLeft size={16} />
        </button>
      )}
    </PanelGroup>
  )
}

export default ClusterConfigsPage
